import { useTranslation } from 'react-i18next';
import { downloadCsv } from './csv';
import { PanelHead, ServiceMark } from './panel';
import { metricText, type Fmt } from './format';
import type { PeriodReport } from './use-report';

/** Costs and results by source (`pa-source-table`): cases per level, cumulative costs per result, ROAS. */
export function ChannelTable({ report, fmt, exportName }: { report: PeriodReport; fmt: Fmt; exportName: string }) {
  const { t } = useTranslation(['performance', 'common']);
  const columns = [
    'source',
    'investment',
    'leads',
    'qualified',
    'meetings',
    'reserved',
    'cpl',
    'cpql',
    'cpMeeting',
    'cpa',
    'roas',
  ] as const;
  const costText = (r: PeriodReport['channels'][number], i: number) => {
    const m = r.economics.costPerLevel[i];
    return m ? metricText(m, (v) => fmt.czk(v)) : '—';
  };
  const exportCsv = () =>
    downloadCsv(
      exportName,
      columns.map((k) => t(`dashboard.sources.${k}`)),
      report.channels.map((r) => [
        t(`channel.${r.channel}`),
        r.spend ? fmt.czk(r.spend.costCzk) : '—',
        fmt.int(r.metrics.leads),
        fmt.int(r.metrics.reached[1] ?? 0),
        fmt.int(r.metrics.reached[2] ?? 0),
        fmt.int(r.metrics.reserved),
        costText(r, 0),
        costText(r, 1),
        costText(r, 2),
        costText(r, 6),
        r.spend ? metricText(r.economics.roas, (v) => fmt.times(v)) : t('dashboard.sources.crm'),
      ]),
    );
  return (
    <section className="pa-panel pa-table-panel" id="pa-finance">
      <PanelHead title={t('dashboard.sources.title')} subtitle={t('dashboard.sources.subtitle')}>
        <label className="pa-field hp-field">
          <span>{t('dashboard.sources.attribution')}</span>
          <select value="last" disabled aria-label={t('dashboard.sources.attribution')}>
            <option value="last">{t('dashboard.sources.attributionLast')}</option>
          </select>
        </label>
      </PanelHead>
      <div className="pa-table-scroll" tabIndex={0}>
        <table className="pa-table hp-table pa-source-table">
          <thead>
            <tr>
              {columns.map((k) => (
                <th key={k} scope="col">
                  {t(`dashboard.sources.${k}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.channels.map((r) => {
              const cost = (i: number) => {
                const m = r.economics.costPerLevel[i];
                return m ? metricText(m, (v) => fmt.czk(v)) : '—';
              };
              return (
                <tr key={r.channel}>
                  <td>
                    <span className="pa-source">
                      <ServiceMark channel={r.channel} />
                      <span>{t(`channel.${r.channel}`)}</span>
                    </span>
                  </td>
                  <td>{r.spend ? fmt.czk(r.spend.costCzk) : '—'}</td>
                  <td>{fmt.int(r.metrics.leads)}</td>
                  <td>{fmt.int(r.metrics.reached[1] ?? 0)}</td>
                  <td>{fmt.int(r.metrics.reached[2] ?? 0)}</td>
                  <td>
                    <b>{fmt.int(r.metrics.reserved)}</b>
                  </td>
                  <td>{cost(0)}</td>
                  <td>{cost(1)}</td>
                  <td>{cost(2)}</td>
                  <td>{cost(6)}</td>
                  <td>
                    {r.spend ? <b>{metricText(r.economics.roas, (v) => fmt.times(v))}</b> : t('dashboard.sources.crm')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="pa-footnote">{t('dashboard.sources.footnote')}</p>
      <div className="hp-card__body">
        <button
          type="button"
          className="hp-button hp-button--secondary"
          onClick={exportCsv}
          disabled={report.channels.length === 0}
        >
          {t('dashboard.exportCsv')}
        </button>
      </div>
    </section>
  );
}
